import { type JSX, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  ARTNAME,
  KATEGORIEN,
  KATEGORIENAME,
  STATUSNAME,
  VORGANGSSTATUS,
  anhangHolen,
  aussortiert as ladeAussortiert,
  gelesenSetzen,
  nachricht as ladeNachricht,
  posteingangsstand,
  uebernehmen,
  vorgaenge as ladeVorgaenge,
  vorgang as ladeVorgang,
  type Anhangszeile,
  type Aussortiertzeile,
  type Kategorie,
  type Listenansicht,
  type Nachrichtzeile,
  type Posteingangsstand,
  type Vorgangsstatus,
  type Vorgangszeile,
} from '../api/support';
import { AbTablet } from '../komponenten/AbTablet';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { markenklasse } from '../marken';
import {
  ANSICHTEN,
  Absagemarke,
  Bearbeitetmarke,
  Faelligkeit,
  Kategorieangabe,
  naechstesZiel,
  Restzeit,
  Vertretungshinweis,
  Zustaendig,
} from './Listenangaben';
import type { Folgewege } from './Folgeaktionen';
import { Vorgangsbearbeitung } from './Vorgangsbearbeitung';
import { statusmarke, Zuordnungsbereich, Zuordnungsmarke } from './Zuordnung';

/**
 * DIE POSTFÄCHER IM CRM (SUP-01, SUP-03 bis SUP-06, SUP-16; Auftrag CRM-176).
 *
 * SEITENTYP LISTE (ÄA-41): Reiter je Postfach — Info und Vertrieb — und
 * „Aussortiert"; die Vorgänge in einer Tabelle, neueste zuerst.
 *
 * DER VORGANG IM GANZEN ARBEITSBEREICH (Auftrag CRM-188; Wunsch vom
 * 10.10.2026: „der platz, mit alles Aktionen, ist zu schmal als rechte
 * spalte"). Ein Klick öffnet ihn unter dem Kopf, die Liste ist dann fort; er
 * hat eine eigene Adresse (`/postfaecher/<id>`), und „← Postfächer" führt zur
 * Liste mit Reiter, Filtern, Suche und Rolllage zurück.
 *
 * DAS CRM FÜHRT (SUP-02): Gelesen, Status und Übernahme gelten hier und
 * spiegeln nicht ins Postfach zurück.
 *
 * FREMDES HTML in einem abgeschotteten Rahmen ohne Skripte und ohne
 * Verbindung nach außen. Externe Bilder lädt erst ein Klick — ein Bild aus
 * dem Netz verriete dem Absender, wann die Nachricht geöffnet wurde.
 *
 * DIE ZUORDNUNG (CRM-177, SUP-07 bis SUP-13): Jeder Vorgang nennt seinen
 * Kunden und wie sicher er erkannt ist. „Klärung" sammelt über beide
 * Postfächer, was keinen Kunden hat — Vorschläge und Unbekanntes.
 * „Unsicher" sammelt, was die Automatik nur über Name oder Domäne
 * zugeordnet hat (SUP-09, entschieden 10.10.2026), zum Bestätigen oder
 * Umordnen.
 *
 * DIE BEARBEITUNG IN DER LISTE (CRM-178): „Meine · Warteschlange · Alle"
 * (SUP-18, SUP-26), Filter nach Kategorie (SUP-14), je Zeile Zuständig,
 * Fälligkeit und Restzeit des nächsten Ziels (SUP-25), wer den Vorgang gerade
 * offen hat (SUP-22) und eine Terminabsage (SUP-48). Die Angaben stehen in
 * `Listenangaben.tsx`.
 *
 * AUF DEM TELEFON lesen (ÄA-63); Status, Übernahme und Zuordnung ab Tablet.
 */

const AUSSORTIERT = '__aussortiert';
/** SUP-13: die Klärungsliste, über alle Postfächer. */
const KLAERUNG = '__klaerung';
/** SUP-09: unsicher zugeordnet, über alle Postfächer. */
const UNSICHER = '__unsicher';

/** Die Reiter heißen nach der Aufgabe, nicht nach der Kennung (Entscheidung vom 10.10.2026). */
const REITERNAME: Readonly<Record<string, string>> = { ALLGEMEIN: 'Info', VERTRIEB: 'Vertrieb' };

function datum(wert: string | null): string {
  if (wert === null) return '—';
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

function absenderText(name: string | null, adresse: string): string {
  return name === null || name === '' ? adresse : `${name} <${adresse}>`;
}

function groesse(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

/** Auftrag CRM-188: der Name des Reiters im Rückweg. */
function reitername(reiter: string, staende: readonly Posteingangsstand[] | null): string {
  if (reiter === KLAERUNG) return 'Klärung';
  if (reiter === UNSICHER) return 'Unsicher';
  if (reiter === AUSSORTIERT) return 'Aussortiert';
  return REITERNAME[reiter] ?? staende?.find((s) => s.kennung === reiter)?.bezeichnung ?? reiter;
}

/** Auftrag CRM-188: was im ganzen Arbeitsbereich offen ist — ein Vorgang oder eine aussortierte Nachricht. */
export type Postfachziel = { readonly art: 'vorgang' | 'nachricht'; readonly id: string };

/**
 * Wie die Adresse dem Ziel folgt (Auftrag CRM-188): `neu` legt einen Eintrag
 * in die Geschichte, `ersetzen` nicht (Blättern), `zurueck` geht den Schritt
 * zurück, der aus der Liste kam — die Liste steht dann nicht zweimal darin.
 */
export type Postfachweg = 'neu' | 'ersetzen' | 'zurueck';

export function Posteingang({
  darfBearbeiten,
  darfZuordnen = false,
  darfKundeAnlegen = false,
  beiKunde,
  vorgang,
  nachricht,
  postfach,
  beiZiel,
  vorbelegung = 'ALLGEMEIN',
  ich,
  folgewege,
}: {
  /** `support/bearbeiten`. Ohne das Recht: nur lesen. */
  readonly darfBearbeiten: boolean;
  /** Der angemeldete Benutzer (SUP-18). */
  readonly ich?: string | undefined;
  /** SUP-23: die Wege in Angebot, Buchung und Rechnung; fehlt einer, fehlt das Recht. */
  readonly folgewege?: Folgewege | undefined;
  /** `support/zuordnen` (SUP-09, SUP-12). */
  readonly darfZuordnen?: boolean;
  /** `kunde/anlegen` — „Als Interessent anlegen" (SUP-10). */
  readonly darfKundeAnlegen?: boolean;
  readonly beiKunde?: ((id: string) => void) | undefined;
  /** Auftrag CRM-188: der offene Vorgang aus der Adresse (SUP-11: auch der Sprung aus der Kundenakte). */
  readonly vorgang?: string | undefined;
  /** Auftrag CRM-188: die offene aussortierte Nachricht aus der Adresse. */
  readonly nachricht?: string | undefined;
  /** SUP-11: das Postfach eines Sprungs — sein Reiter geht der Vorbelegung vor. */
  readonly postfach?: string | undefined;
  /**
   * Auftrag CRM-188: Öffnen, Blättern und Zurück setzen die Adresse. Ohne
   * diesen Weg führt die Maske das Ziel selbst (Tests, Maskenschau).
   */
  readonly beiZiel?: ((ziel: Postfachziel | null, weg: Postfachweg) => void) | undefined;
  /** Auftrag CRM-185: der Reiter zu Beginn nach Abteilung (`postfachVorbelegung`); ein Sprung geht vor. */
  readonly vorbelegung?: string;
}): JSX.Element {
  const ausAdresse: Postfachziel | null = vorgang !== undefined
    ? { art: 'vorgang', id: vorgang }
    : nachricht !== undefined ? { art: 'nachricht', id: nachricht } : null;
  const [eigenesZiel, setzeEigenesZiel] = useState(ausAdresse);
  const ziel = beiZiel !== undefined ? ausAdresse : eigenesZiel;
  const offen = ziel !== null;

  const [staende, setzeStaende] = useState<readonly Posteingangsstand[] | null>(null);
  const [takt, setzeTakt] = useState(2);
  const [reiter, setzeReiter] = useState(nachricht !== undefined ? AUSSORTIERT : postfach ?? vorbelegung);
  const [suche, setzeSuche] = useState('');
  const [status, setzeStatus] = useState<Vorgangsstatus | null>(null);
  const [nurUngelesen, setzeNurUngelesen] = useState(false);
  /** SUP-18: Meine, Warteschlange oder Alle — „Meine" schließt Vertretungen ein (SUP-26). */
  const [ansicht, setzeAnsicht] = useState<Listenansicht>('alle');
  /** SUP-14: Filter nach Kategorie. */
  const [kategorie, setzeKategorie] = useState<Kategorie | null>(null);
  const [liste, setzeListe] = useState<readonly Vorgangszeile[] | null>(null);
  const [aussortiertListe, setzeAussortiertListe] = useState<readonly Aussortiertzeile[] | null>(null);
  const [gesamt, setzeGesamt] = useState(0);
  /** Der zuletzt geöffnete Eintrag — nach dem Rückweg in der Liste markiert. */
  const [zuletzt, setzeZuletzt] = useState<string | null>(ziel?.id ?? null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  /*
   * Auftrag CRM-188: DER RÜCKWEG FINDET DIE LISTE WIEDER. Die Maske bleibt
   * stehen, während der Vorgang offen ist — Reiter, Filter, Suche und die
   * geladene Liste bleiben im Zustand. Die Rolllage merkt sich der Klick in
   * der Liste; `ausListe` ist der Eintrag, hinter dem in der Geschichte die
   * Liste liegt (dann geht der Rückweg den Schritt zurück, statt einen
   * neuen anzulegen).
   */
  const rolllage = useRef<number | null>(null);
  const ausListe = useRef<string | null>(null);
  const listeGesehen = useRef(!offen);
  const warOffen = useRef(offen);
  const rueckweg = useRef<HTMLButtonElement>(null);
  const tabelle = useRef<HTMLDivElement>(null);

  const oeffne = useCallback((neu: Postfachziel | null, weg: Postfachweg): void => {
    if (beiZiel !== undefined) beiZiel(neu, weg);
    else setzeEigenesZiel(neu);
  }, [beiZiel]);

  const ausDerListe = (neu: Postfachziel): void => {
    rolllage.current = window.scrollY;
    ausListe.current = neu.id;
    oeffne(neu, 'neu');
  };

  const zurListe = (): void => {
    oeffne(null, ziel !== null && ausListe.current === ziel.id ? 'zurueck' : 'neu');
  };

  useEffect(() => {
    if (ziel !== null) setzeZuletzt(ziel.id);
    else listeGesehen.current = true;
  }, [ziel?.id]);

  /* Ein Sprung (Glocke, Kundenakte) bringt sein Postfach mit; eine aussortierte Nachricht steht unter „Aussortiert". */
  useEffect(() => {
    if (nachricht !== undefined) setzeReiter(AUSSORTIERT);
    else if (postfach !== undefined) setzeReiter(postfach);
  }, [postfach, vorgang, nachricht]);

  /*
   * Öffnen: oben beginnen, der Fokus auf den Rückweg — der angeklickte Knopf
   * der Liste ist fort (WCAG 2.4.3). Zurück: die alte Rolllage, der Fokus auf
   * der Zeile, aus der man kam.
   */
  useLayoutEffect(() => {
    if (offen === warOffen.current) return;
    warOffen.current = offen;
    if (offen) {
      window.scrollTo(0, 0);
      rueckweg.current?.focus({ preventScroll: true });
      return;
    }
    window.scrollTo(0, rolllage.current ?? 0);
    if (zuletzt !== null) {
      tabelle.current
        ?.querySelector<HTMLButtonElement>(`button[data-kennung="${CSS.escape(zuletzt)}"]`)
        ?.focus({ preventScroll: true });
    }
  }, [offen, zuletzt]);

  const fange = useCallback((f: unknown) => {
    setzeFehler(f instanceof ApiFehler ? f : null);
    if (!(f instanceof ApiFehler)) throw f;
  }, []);

  const standHolen = useCallback(async () => {
    try {
      const antwort = await posteingangsstand();
      setzeStaende(antwort.postfaecher);
      setzeTakt(antwort.taktMinuten);
    } catch (f) {
      setzeStaende([]);
      fange(f);
    }
  }, [fange]);

  const holen = useCallback(async () => {
    try {
      if (reiter === AUSSORTIERT) {
        const antwort = await ladeAussortiert(null);
        setzeAussortiertListe(antwort.nachrichten);
        setzeGesamt(antwort.gesamt);
      } else {
        const antwort = await ladeVorgaenge(reiter === KLAERUNG || reiter === UNSICHER
          ? { postfach: null, zuordnung: reiter === KLAERUNG ? 'offen' : 'unsicher', suche, status, nurUngelesen, ansicht, kategorie }
          : { postfach: reiter, suche, status, nurUngelesen, ansicht, kategorie });
        setzeListe(antwort.vorgaenge);
        setzeGesamt(antwort.gesamt);
      }
      setzeFehler(null);
    } catch (f) {
      setzeListe([]);
      setzeAussortiertListe([]);
      fange(f);
    }
  }, [reiter, suche, status, nurUngelesen, ansicht, kategorie, fange]);

  useEffect(() => {
    void standHolen();
  }, [standHolen]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const istAussortiert = reiter === AUSSORTIERT;
  const istKlaerung = reiter === KLAERUNG;
  const istUnsicher = reiter === UNSICHER;
  const geladen = istAussortiert ? aussortiertListe : liste;
  useZaehler(geladen === null ? undefined : istAussortiert
    ? zaehlwort(gesamt, 'aussortierte Nachricht', 'aussortierte Nachrichten')
    : zaehlwort(gesamt, 'Vorgang', 'Vorgänge'));

  const stand = staende?.find((s) => s.kennung === reiter);
  const aussortiertGesamt = (staende ?? []).reduce((summe, s) => summe + s.aussortiert, 0);

  const wechsle = (wert: string): void => {
    setzeReiter(wert);
    setzeZuletzt(null);
    setzeListe(null);
    setzeAussortiertListe(null);
  };

  const neuLaden = (): void => {
    void holen();
    void standHolen();
  };

  if (ziel !== null) {
    /* Auftrag CRM-188: Blättern in der Liste, aus der man kam — in ihrer Reihenfolge. */
    const kennungen = (geladen ?? []).map((z) => z.id);
    const stelle = kennungen.indexOf(ziel.id);
    const blaettere = (um: number): void => {
      const naechste = kennungen[stelle + um];
      if (naechste === undefined) return;
      if (ausListe.current === ziel.id) ausListe.current = naechste;
      oeffne({ art: ziel.art, id: naechste }, 'ersetzen');
    };
    const wort = ziel.art === 'nachricht' ? 'Nachricht' : 'Vorgang';
    return (
      <>
        {fehler !== null && <Meldung fehler={fehler} />}
        <nav className="vorgangsseite__weg" aria-label={`${wort} und Liste`}>
          <button type="button" className="knopf knopf--klein" data-auch-telefon ref={rueckweg} onClick={zurListe}>
            ← Postfächer · {reitername(reiter, staende)}
          </button>
          {stelle >= 0 && kennungen.length > 1 && (
            <span className="vorgangsseite__blaettern">
              <button
                type="button"
                className="knopf knopf--klein"
                data-auch-telefon
                aria-label={ziel.art === 'nachricht' ? 'Vorige Nachricht' : 'Voriger Vorgang'}
                disabled={stelle === 0}
                onClick={() => {
                  blaettere(-1);
                }}
              >
                ‹
              </button>
              <span className="hilfe">
                {String(stelle + 1)} von {String(kennungen.length)}
              </span>
              <button
                type="button"
                className="knopf knopf--klein"
                data-auch-telefon
                aria-label={ziel.art === 'nachricht' ? 'Nächste Nachricht' : 'Nächster Vorgang'}
                disabled={stelle === kennungen.length - 1}
                onClick={() => {
                  blaettere(1);
                }}
              >
                ›
              </button>
            </span>
          )}
        </nav>
        {ziel.art === 'nachricht' ? (
          <Aussortiertansicht
            id={ziel.id}
            darfBearbeiten={darfBearbeiten}
            beiAenderung={() => {
              zurListe();
              neuLaden();
            }}
          />
        ) : (
          <Vorgangsansicht
            id={ziel.id}
            darfBearbeiten={darfBearbeiten}
            darfZuordnen={darfZuordnen}
            darfKundeAnlegen={darfKundeAnlegen}
            beiKunde={beiKunde}
            ich={ich}
            folgewege={folgewege}
            beiVorgang={(id) => {
              oeffne({ art: 'vorgang', id }, 'neu');
            }}
            beiGeladen={(v) => {
              /* Direkt über die Adresse geöffnet: der Rückweg führt in das Postfach des Vorgangs. */
              if (reiter === AUSSORTIERT || (!listeGesehen.current && postfach === undefined)) setzeReiter(v.postfach);
            }}
            beiAenderung={neuLaden}
          />
        )}
      </>
    );
  }

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Segmente
        beschriftung="Postfach"
        gewaehlt={reiter}
        beiWahl={wechsle}
        optionen={[
          ...(staende ?? []).map((s) => ({
            wert: s.kennung,
            text: `${REITERNAME[s.kennung] ?? s.bezeichnung}${s.ungelesen > 0 ? ` (${String(s.ungelesen)})` : ''}`,
          })),
          { wert: KLAERUNG, text: 'Klärung' },
          { wert: UNSICHER, text: 'Unsicher' },
          { wert: AUSSORTIERT, text: `Aussortiert${aussortiertGesamt > 0 ? ` (${String(aussortiertGesamt)})` : ''}` },
        ]}
      />

      <Liste
        {...(istAussortiert
          ? {}
          : {
            suche: {
              wert: suche,
              aendern: setzeSuche,
              beschriftung: 'Vorgänge durchsuchen',
              platzhalter: 'Absender, Betreff, Nummer oder Text',
            },
          })}
        filter={
          istAussortiert ? undefined : (
            <>
              {/* SUP-18, SUP-26: eigene Vorgänge samt Vertretungen, die Warteschlange oder alle. */}
              <Segmente<Listenansicht>
                beschriftung="Ansicht"
                gewaehlt={ansicht}
                beiWahl={setzeAnsicht}
                optionen={ANSICHTEN}
              />
              {/* SUP-14 */}
              <select
                className="liste__filter"
                value={kategorie ?? ''}
                onChange={(e) => {
                  setzeKategorie(e.target.value === '' ? null : (e.target.value as Kategorie));
                }}
                aria-label="Kategorie"
              >
                <option value="">Alle Kategorien</option>
                {KATEGORIEN.map((k) => (
                  <option key={k} value={k}>
                    {KATEGORIENAME[k]}
                  </option>
                ))}
              </select>
              <select
                className="liste__filter"
                value={status ?? ''}
                onChange={(e) => {
                  setzeStatus(e.target.value === '' ? null : (e.target.value as Vorgangsstatus));
                }}
                aria-label="Status"
              >
                <option value="">Alle Status</option>
                {VORGANGSSTATUS.map((s) => (
                  <option key={s} value={s}>
                    {STATUSNAME[s]}
                  </option>
                ))}
              </select>
              <label className="liste__schalter">
                <input
                  type="checkbox"
                  checked={nurUngelesen}
                  onChange={(e) => {
                    setzeNurUngelesen(e.target.checked);
                  }}
                />
                <span>Nur ungelesene</span>
              </label>
            </>
          )
        }
        hinweise={istKlaerung ? (
          <p className="hilfe">
            Vorgänge beider Postfächer ohne Kunden: Vorschläge, aus denen jemand wählt, und Nachrichten,
            zu denen kein Kunde passt. Zuordnen, einen Kunden anlegen oder neu prüfen lassen.
          </p>
        ) : istUnsicher ? (
          <p className="hilfe">
            Vorgänge beider Postfächer, die automatisch zugeordnet sind, aber nur über den Namen des Absenders
            oder die Domäne seiner Adresse. Bestätigen oder einem anderen Kunden zuordnen.
          </p>
        ) : <Abrufhinweis stand={stand} aussortiert={istAussortiert} takt={takt} />}
      >
        {geladen === null && <p role="status">Wird geladen …</p>}
        {geladen !== null && geladen.length === 0 && (
          <p className="liste__leer">
            {istAussortiert
              ? 'Nichts aussortiert.'
              : suche !== '' || status !== null || nurUngelesen || kategorie !== null
                ? 'Kein Vorgang passt zum Filter.'
                : ansicht === 'meine'
                  ? 'Kein eigener Vorgang — auch keiner aus einer Vertretung.'
                  : ansicht === 'warteschlange'
                    ? 'Die Warteschlange ist leer — jeder Vorgang hat einen Zuständigen.'
                    : istKlaerung
                      ? 'Nichts zu klären — jeder Vorgang hat seinen Kunden.'
                      : istUnsicher
                        ? 'Keine unsichere Zuordnung offen.'
                        : 'Noch kein Vorgang in diesem Postfach.'}
          </p>
        )}
        {!istAussortiert && liste !== null && liste.length > 0 && (
          <div className="posteingang__rahmen" ref={tabelle}>
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle posteingang__tabelle" aria-label="Vorgänge">
                <thead>
                  <tr>
                    <th scope="col" className="liste__spalte--knapp">Letzte Nachricht</th>
                    <th scope="col">Absender</th>
                    <th scope="col" className="posteingang__betreff">Betreff</th>
                    <th scope="col" className="posteingang__nebenspalte">Kunde</th>
                    <th scope="col" className="posteingang__nebenspalte">Zuständig</th>
                    <th scope="col" className="posteingang__nebenspalte">Fällig</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.map((v) => (
                    <tr
                      key={v.id}
                      className={[
                        zuletzt === v.id ? 'liste__zeile--gewaehlt' : '',
                        v.gelesen ? '' : 'liste__zeile--ungelesen',
                      ].join(' ').trim() || undefined}
                    >
                      <th scope="row">
                        <button
                          type="button"
                          className="liste__oeffnen"
                          data-kennung={v.id}
                          onClick={() => {
                            ausDerListe({ art: 'vorgang', id: v.id });
                          }}
                        >
                          <span className="liste__einzeilig">{datum(v.letzteNachrichtAm)}</span>
                        </button>
                        <br />
                        <span className="hilfe">
                          {v.nummer}
                          {istKlaerung && ` · ${REITERNAME[v.postfach] ?? v.postfach}`}
                        </span>
                        {/* SUP-48: in der ersten Spalte, damit sie auch auf dem Telefon ohne Rollen zu sehen ist. */}
                        {v.bearbeitung.absage !== null && (
                          <>
                            <br />
                            <Absagemarke absage={v.bearbeitung.absage} />
                          </>
                        )}
                      </th>
                      <td>
                        {/* Auftrag CRM-187: Name und Adresse je eine Zeile, gekürzt; ganz im Kurztext. */}
                        <div className="posteingang__absender">
                          <span className="liste__name posteingang__zeile" title={v.absendername ?? v.absender}>
                            {v.absendername ?? v.absender}
                          </span>
                          {v.absendername !== null && (
                            <span className="hilfe posteingang__zeile" title={v.absender}>{v.absender}</span>
                          )}
                        </div>
                      </td>
                      <td className="posteingang__betreff">
                        {/* Auftrag CRM-187: der Betreff einzeilig, der Auszug höchstens zwei Zeilen. */}
                        <div className="posteingang__text">
                          <span className="posteingang__zeile" title={v.betreff}>
                            {v.gelesen ? v.betreff : (
                              <>
                                <span className={markenklasse('aufmerksamkeit')}>ungelesen</span>{' '}
                                <strong>{v.betreff}</strong>
                              </>
                            )}
                          </span>
                          <span className="hilfe posteingang__auszug">
                            {v.auszug}
                            {v.nachrichten > 1 && ` · ${String(v.nachrichten)} Nachrichten`}
                            {v.anhaenge > 0 && ` · ${String(v.anhaenge)} ${v.anhaenge === 1 ? 'Anhang' : 'Anhänge'}`}
                          </span>
                          {/* SUP-22: wer den Vorgang gerade offen hat; SUP-26: für wen man ihn vertritt. */}
                          {(v.bearbeitung.bearbeitetVon !== null
                            || (ansicht === 'meine' && v.bearbeitung.zustaendigkeit.vertretenDurch !== null)) && (
                            <span>
                              <Bearbeitetmarke von={v.bearbeitung.bearbeitetVon} />{' '}
                              <Vertretungshinweis zustaendigkeit={v.bearbeitung.zustaendigkeit} ansicht={ansicht} />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="posteingang__nebenspalte">
                        {v.kundenname !== null && (
                          <>
                            <span className="liste__einzeilig">{v.kundenname}</span>
                            <br />
                          </>
                        )}
                        <Zuordnungsmarke zuordnung={v.zuordnung} sicherheit={v.sicherheit} />
                      </td>
                      <td className="posteingang__nebenspalte">
                        {/* SUP-18, SUP-26 */}
                        <Zustaendig zustaendigkeit={v.bearbeitung.zustaendigkeit} />
                      </td>
                      <td className="posteingang__nebenspalte">
                        {/* SUP-17, SUP-25 */}
                        <Faelligkeit am={v.bearbeitung.faelligAm} offen={v.status !== 'geloest' && v.status !== 'geschlossen'} />
                        {naechstesZiel(v.bearbeitung) !== null && (
                          <>
                            <br />
                            <Restzeit stand={v.bearbeitung} />
                          </>
                        )}
                      </td>
                      <td>
                        {statusmarke(v.status)}
                        <br />
                        {/* SUP-14 */}
                        <Kategorieangabe kategorie={v.bearbeitung.kategorie} vorschlag={v.bearbeitung.kategorieVorschlag} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {istAussortiert && aussortiertListe !== null && aussortiertListe.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon" ref={tabelle}>
            <table className="liste__tabelle" aria-label="Aussortierte Nachrichten">
              <thead>
                <tr>
                  <th scope="col" className="liste__spalte--knapp">Eingang</th>
                  <th scope="col">Absender</th>
                  <th scope="col" className="posteingang__betreff">Betreff</th>
                  <th scope="col">Grund</th>
                </tr>
              </thead>
              <tbody>
                {aussortiertListe.map((n) => (
                  <tr key={n.id} className={zuletzt === n.id ? 'liste__zeile--gewaehlt' : undefined}>
                    <th scope="row">
                      <button
                        type="button"
                        className="liste__oeffnen"
                        data-kennung={n.id}
                        onClick={() => {
                          ausDerListe({ art: 'nachricht', id: n.id });
                        }}
                      >
                        <span className="liste__einzeilig">{datum(n.eingegangenAm)}</span>
                      </button>
                      <br />
                      <span className="hilfe">{REITERNAME[n.postfach] ?? n.postfach}</span>
                    </th>
                    <td>{absenderText(n.absendername, n.absender)}</td>
                    <td className="posteingang__betreff">
                      <div className="posteingang__text">
                        <span className="posteingang__zeile" title={n.betreff}>{n.betreff}</span>
                        <span className="hilfe posteingang__auszug">{n.auszug}</span>
                      </div>
                    </td>
                    <td>
                      <span className={markenklasse('stillgelegt')}>{ARTNAME[n.art]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>
    </>
  );
}

/** SUP-01: ob das Postfach abgerufen wird — und wenn nicht, warum. */
function Abrufhinweis({
  stand,
  aussortiert,
  takt,
}: {
  readonly stand: Posteingangsstand | undefined;
  readonly aussortiert: boolean;
  readonly takt: number;
}): JSX.Element {
  if (aussortiert) {
    return (
      <p className="hilfe">
        Werbung, Massenversand und automatische Antworten ohne passenden Vorgang. Sie bleiben eine
        Zeit lang hier und werden dann entfernt; im Postfach selbst bleibt alles liegen. Eine
        fälschlich aussortierte Nachricht lässt sich als Vorgang übernehmen.
      </p>
    );
  }
  if (stand === undefined) return <></>;
  if (!stand.eingerichtet) {
    // CRM-183: Der Hinweis nennt den Grund, wenn es nicht an fehlenden Zugangsdaten liegt.
    return stand.hinweis !== null ? (
      <p className="warnung" role="status">
        <strong>Nicht eingerichtet.</strong> {stand.hinweis}
      </p>
    ) : (
      <p className="warnung" role="status">
        <strong>Nicht eingerichtet.</strong> Für dieses Postfach sind auf dieser Instanz keine
        Zugangsdaten hinterlegt; es wird nicht abgerufen. Die Administration trägt sie in die
        Betriebskonfiguration ein: {stand.variablen.join(', ')}.
      </p>
    );
  }
  if (stand.adapter === 'demo' && stand.fehler === null) {
    return (
      <p className="hinweiszeile" role="status">
        <strong>Demo-Anbindung.</strong> Dieses Postfach ist mit keinem Mailserver verbunden. Mails
        kommen über „Demo-Mail einspielen“ in Verwaltung › Postfächer; Antworten gehen in den
        Demo-Ausgang und werden nie zugestellt.
      </p>
    );
  }
  if (stand.fehler !== null) {
    return (
      <p className="warnung" role="status">
        <strong>Der letzte Abruf ist gescheitert</strong> ({datum(stand.gelaufenAm)}): {stand.fehler}
        {stand.naechsterAbrufAb !== null && <> Nächster Versuch ab {datum(stand.naechsterAbrufAb)}.</>}
      </p>
    );
  }
  return (
    <p className="hilfe">
      Abgerufen alle {String(takt)} Minuten, zuletzt {datum(stand.erfolgreichAm)}. Das Postfach selbst
      bleibt unverändert — gelesen, Status und Übernahme gelten nur im CRM.
    </p>
  );
}

/**
 * SUP-03, SUP-04: der Vorgang mit seinem Verlauf, älteste Nachricht zuerst.
 *
 * Auftrag CRM-188: im ganzen Arbeitsbereich und nicht mehr als Seitenteil der
 * Liste — der Aufbau der Akte (Verlauf links breit, Bearbeitung rechts) steht
 * in `Vorgangsbearbeitung`.
 */
function Vorgangsansicht({
  id,
  darfBearbeiten,
  darfZuordnen,
  darfKundeAnlegen,
  beiKunde,
  ich,
  folgewege,
  beiVorgang,
  beiGeladen,
  beiAenderung,
}: {
  readonly id: string;
  readonly darfBearbeiten: boolean;
  readonly darfZuordnen: boolean;
  readonly darfKundeAnlegen: boolean;
  readonly beiKunde: ((id: string) => void) | undefined;
  readonly ich: string | undefined;
  readonly folgewege: Folgewege | undefined;
  readonly beiVorgang: (id: string) => void;
  /** Der Vorgang ist geladen — die Liste stellt ihren Reiter danach. */
  readonly beiGeladen: (v: Vorgangszeile) => void;
  readonly beiAenderung: () => void;
}): JSX.Element {
  const [daten, setzeDaten] = useState<{
    readonly vorgang: Vorgangszeile;
    readonly nachrichten: readonly Nachrichtzeile[];
  } | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  useEffect(() => {
    setzeDaten(null);
    setzeFehler(null);
    let gilt = true;
    void (async () => {
      try {
        const geladen = await ladeVorgang(id);
        if (!gilt) return;
        setzeDaten(geladen);
        beiGeladen(geladen.vorgang);
        // Wer den Vorgang öffnet, hat ihn gelesen — nur im CRM (SUP-02).
        if (darfBearbeiten && !geladen.vorgang.gelesen) {
          await gelesenSetzen(id, true);
          if (gilt) beiAenderung();
        }
      } catch (f) {
        if (!gilt) return;
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      }
    })();
    return () => {
      gilt = false;
    };
    // beiAenderung und beiGeladen wechseln mit jeder Darstellung der Liste; geladen wird nur bei neuer Kennung.
  }, [id, darfBearbeiten]);

  const tue = async (was: () => Promise<void>): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await was();
      setzeDaten(await ladeVorgang(id));
      beiAenderung();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  if (daten === null) {
    return fehler === null ? <p role="status">Wird geladen …</p> : <Meldung fehler={fehler} />;
  }
  const { vorgang: v, nachrichten } = daten;

  return (
    <>
      <div className="vorgangsseite__titel">
        <h2>{v.betreff || '(ohne Betreff)'}</h2>
        <p>
          <span className="hilfe">{v.nummer}</span> {statusmarke(v.status)}{' '}
          <span className="hilfe">eingegangen am {datum(v.eingangAm)}</span>
        </p>
      </div>
      {fehler !== null && <Meldung fehler={fehler} />}

      {/* SUP-14 bis SUP-27: Leiste, Antwort, Notizen, Folgeaktionen; Status steht in der Leiste. */}
      <Vorgangsbearbeitung
        vorgang={v}
        nachrichten={nachrichten}
        darfBearbeiten={darfBearbeiten}
        ich={ich}
        folgewege={folgewege}
        beiVorgang={beiVorgang}
        beiKunde={beiKunde}
        beiAenderung={async () => {
          setzeDaten(await ladeVorgang(v.id));
          beiAenderung();
        }}
        handlungen={darfBearbeiten ? (
          <AbTablet teil>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--klein"
                data-auch-telefon
                disabled={laeuft}
                onClick={() => void tue(() => gelesenSetzen(v.id, false))}
              >
                Als ungelesen markieren
              </button>
            </div>
          </AbTablet>
        ) : undefined}
        zuordnung={
          <Zuordnungsbereich
            vorgang={v.id}
            darfZuordnen={darfZuordnen}
            darfKundeAnlegen={darfKundeAnlegen}
            beiKunde={beiKunde}
            beiAenderung={() => {
              // CRM-196: Der nächste Schritt hängt an der Zuordnung — der Vorgang lädt mit.
              ladeVorgang(v.id).then(setzeDaten, () => {
                // Scheitert das Nachladen, bleibt der alte Stand; die Zuordnung selbst ist gespeichert.
              });
              beiAenderung();
            }}
          />
        }
        nachricht={(n) => <Nachrichtansicht nachricht={n} />}
      />
    </>
  );
}

/** SUP-16: eine aussortierte Nachricht — ansehen und auf Wunsch als Vorgang übernehmen (Auftrag CRM-188: im ganzen Arbeitsbereich). */
function Aussortiertansicht({
  id,
  darfBearbeiten,
  beiAenderung,
}: {
  readonly id: string;
  readonly darfBearbeiten: boolean;
  readonly beiAenderung: () => void;
}): JSX.Element {
  const [n, setzeN] = useState<Nachrichtzeile | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  useEffect(() => {
    setzeN(null);
    setzeFehler(null);
    ladeNachricht(id).then(setzeN, (f: unknown) => {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    });
  }, [id]);

  if (n === null) {
    return fehler === null ? <p role="status">Wird geladen …</p> : <Meldung fehler={fehler} />;
  }

  return (
    <>
      <div className="vorgangsseite__titel">
        <h2>{n.betreff || '(ohne Betreff)'}</h2>
        <p>
          <span className={markenklasse('stillgelegt')}>{ARTNAME[n.art]}</span>
        </p>
      </div>
      {fehler !== null && <Meldung fehler={fehler} />}
      <div className="akte akte--eine-spalte">
        <section className="akte__haupt" aria-label="Nachricht">
          {darfBearbeiten && (
            <AbTablet teil>
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--haupt knopf--klein"
                  disabled={laeuft}
                  onClick={() => {
                    setzeLaeuft(true);
                    setzeFehler(null);
                    uebernehmen(n.id)
                      .then(beiAenderung, (f: unknown) => {
                        setzeFehler(f instanceof ApiFehler ? f : null);
                        if (!(f instanceof ApiFehler)) throw f;
                      })
                      .finally(() => {
                        setzeLaeuft(false);
                      });
                  }}
                >
                  Als Vorgang übernehmen
                </button>
              </div>
            </AbTablet>
          )}
          <Nachrichtansicht nachricht={n} />
        </section>
      </div>
    </>
  );
}

function Nachrichtansicht({ nachricht: n }: { readonly nachricht: Nachrichtzeile }): JSX.Element {
  return (
    <article className="mailnachricht" aria-label={`Nachricht von ${n.absender}`}>
      <dl className="mailnachricht__kopf">
        <dt>Von</dt>
        <dd>{absenderText(n.absendername, n.absender)}</dd>
        <dt>An</dt>
        <dd>{n.an.join(', ') || '—'}</dd>
        {n.kopie.length > 0 && (
          <>
            <dt>Kopie</dt>
            <dd>{n.kopie.join(', ')}</dd>
          </>
        )}
        <dt>{n.richtung === 'ausgehend' ? 'Gesendet' : 'Eingang'}</dt>
        <dd>
          {datum(n.eingegangenAm)}
          {n.art !== 'nachricht' && (
            <>
              {' '}
              <span className={markenklasse('stillgelegt')}>{ARTNAME[n.art]}</span>
            </>
          )}
        </dd>
      </dl>
      {n.textHtml !== null ? (
        <Mailtext html={n.textHtml} betreff={n.betreff} />
      ) : (
        <pre className="mailnachricht__text">{n.textRein ?? ''}</pre>
      )}
      {n.anhaenge.filter((a) => !a.eingebettet || a.zustand !== 'abgelegt').length > 0 && (
        <ul className="kanalliste" aria-label="Anhänge">
          {n.anhaenge
            .filter((a) => !a.eingebettet || a.zustand !== 'abgelegt')
            .map((a) => (
              <Anhangzeile key={a.id} anhang={a} />
            ))}
        </ul>
      )}
    </article>
  );
}

/** Was der Browser selbst zeigt; alles andere wird gespeichert, nie ausgeführt (SUP-06). */
const ANZEIGBAR = /^(application\/pdf|image\/(png|jpeg|gif|webp)|text\/plain)$/i;

function Anhangzeile({ anhang: a }: { readonly anhang: Anhangszeile }): JSX.Element {
  const [fehler, setzeFehler] = useState<string | null>(null);

  const oeffnen = async (): Promise<void> => {
    setzeFehler(null);
    try {
      const blob = await anhangHolen(a.id);
      const adresse = URL.createObjectURL(blob);
      const verweis = document.createElement('a');
      verweis.href = adresse;
      verweis.rel = 'noopener';
      if (ANZEIGBAR.test(a.inhaltstyp)) verweis.target = '_blank';
      else verweis.download = a.dateiname;
      verweis.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(adresse);
      }, 60_000);
    } catch (f) {
      setzeFehler(f instanceof Error ? f.message : 'Der Anhang ließ sich nicht öffnen.');
    }
  };

  return (
    <li>
      {a.zustand === 'abgelegt' ? (
        <button type="button" className="knopf knopf--klein" data-auch-telefon onClick={() => void oeffnen()}>
          {a.dateiname}
        </button>
      ) : (
        <span>{a.dateiname}</span>
      )}{' '}
      <span className="hilfe">{groesse(a.groesse)}</span>
      {a.grund !== null && (
        <>
          <br />
          <span className="hilfe">{a.grund}</span>
        </>
      )}
      {fehler !== null && (
        <>
          <br />
          <span className="feld__fehler">{fehler}</span>
        </>
      )}
    </li>
  );
}

/**
 * Das HTML einer Nachricht im abgeschotteten Rahmen: `sandbox` ohne Skripte
 * und ohne eigenen Ursprung, dazu eine Inhaltsrichtlinie, die nur
 * eingebettete Bilder (data:) erlaubt. Erst auf Klick kommen externe Bilder
 * dazu — dann auch von https:.
 */
export function Mailtext({ html, betreff }: { readonly html: string; readonly betreff: string }): JSX.Element {
  const [extern, setzeExtern] = useState(false);
  const hatExterne = html.includes('data-extern-src=');
  const koerper = extern ? html.replaceAll('data-extern-src=', 'src=') : html;
  const richtlinie = `default-src 'none'; img-src data:${extern ? ' https:' : ''}; style-src 'unsafe-inline'`;
  const dokument =
    `<!doctype html><html><head><meta charset="utf-8">`
    + `<meta http-equiv="Content-Security-Policy" content="${richtlinie}">`
    + '<style>body{font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;margin:8px;'
    + 'color:#1a1a1a;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style>'
    + `</head><body>${koerper}</body></html>`;

  return (
    <div className="mailtext">
      {hatExterne && !extern && (
        <p className="hinweiszeile">
          Externe Bilder sind nicht geladen.{' '}
          <button
            type="button"
            className="knopf knopf--klein"
            data-auch-telefon
            onClick={() => {
              setzeExtern(true);
            }}
          >
            Externe Bilder laden
          </button>
        </p>
      )}
      <iframe
        className="mailtext__rahmen"
        title={`Text der Nachricht: ${betreff}`}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        srcDoc={dokument}
      />
    </div>
  );
}
